import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../utils/axios-auth';
import { createWhiteboard, getWhiteboardByMeetingId } from '../services/whiteboard-service';
import { getUserFacingMessageFromAxiosError } from '../utils/user-facing-api-error';
// Async thunks
export const get_meetings = createAsyncThunk('meeting/get_meetings', async (_, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.get('/meetings');
        if (!response.data.success) {
            throw new Error('Failed to fetch meetings');
        }
        return response.data.data;
    }
    catch (error) {
        return rejectWithValue(getUserFacingMessageFromAxiosError(error, { fallback: 'An error occurred while fetching meetings' }));
    }
});
export const start_meeting = createAsyncThunk(// meeting_id is UUID
'meeting/start_meeting', async (meeting_id, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.post(`/meetings/${meeting_id}/start`);
        if (!response.data.success) {
            throw new Error('Failed to start meeting');
        }
        return response.data.data;
    }
    catch (error) {
        return rejectWithValue(getUserFacingMessageFromAxiosError(error, { fallback: 'An error occurred while starting the meeting' }));
    }
});
export const join_meeting = createAsyncThunk(// meeting_id is UUID
'meeting/join_meeting', async (meeting_id, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.post(`/meetings/${meeting_id}/join`);
        if (!response.data.success) {
            throw new Error('Failed to join meeting');
        }
        return response.data.data;
    }
    catch (error) {
        return rejectWithValue(getUserFacingMessageFromAxiosError(error, { fallback: 'An error occurred while joining the meeting' }));
    }
});
export const end_meeting = createAsyncThunk(// meeting_id is UUID
'meeting/end_meeting', async (meeting_id, { rejectWithValue }) => {
    try {
        await axiosInstance.post(`/meetings/${meeting_id}/end`);
    }
    catch (error) {
        return rejectWithValue(getUserFacingMessageFromAxiosError(error, { fallback: 'An error occurred while ending the meeting' }));
    }
});
export const leave_meeting = createAsyncThunk(// meeting_id is UUID
'meeting/leave_meeting', async (meeting_id, { rejectWithValue }) => {
    try {
        await axiosInstance.post(`/meetings/${meeting_id}/leave`);
    }
    catch (error) {
        return rejectWithValue(getUserFacingMessageFromAxiosError(error, { fallback: 'An error occurred while leaving the meeting' }));
    }
});
export const delete_meeting = createAsyncThunk(// meeting_id is UUID
'meeting/delete_meeting', async (meeting_id, { rejectWithValue }) => {
    try {
        await axiosInstance.delete(`/meetings/${meeting_id}`);
    }
    catch (error) {
        return rejectWithValue(getUserFacingMessageFromAxiosError(error, { fallback: 'An error occurred while deleting the meeting' }));
    }
});
export const restore_meeting = createAsyncThunk(// meeting_id is UUID
'meeting/restore_meeting', async (meeting_id, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.post(`/meetings/${meeting_id}/restore`);
        if (!response.data.success) {
            throw new Error('Failed to restore meeting');
        }
        return response.data.data;
    }
    catch (error) {
        return rejectWithValue(getUserFacingMessageFromAxiosError(error, { fallback: 'An error occurred while restoring the meeting' }));
    }
});
// New thunk to ensure a meeting has a whiteboard
export const ensure_meeting_whiteboard = createAsyncThunk('meeting/ensure_meeting_whiteboard', async (meeting_id, { rejectWithValue, getState }) => {
    try {
        // First try to get the current meeting from state
        const state = getState();
        let meeting = state.meeting.current_meeting;
        // If meeting is not in state or doesn't match the requested meeting_id, get it from API
        if (!meeting || meeting.id !== meeting_id) {
            const response = await axiosInstance.get(`/meetings/${meeting_id}`);
            if (!response.data.success) {
                throw new Error('Failed to fetch meeting');
            }
            meeting = response.data.data;
        }
        // If meeting already has a whiteboard, return it
        if (meeting.active_whiteboard_id) {
            return meeting;
        }
        // Try to find existing whiteboard for this meeting
        const existingWhiteboard = await getWhiteboardByMeetingId(meeting_id);
        if (existingWhiteboard) {
            // Update meeting with the whiteboard ID
            const updateResponse = await axiosInstance.put(`/meetings/${meeting_id}`, {
                active_whiteboard_id: existingWhiteboard.id
            });
            if (!updateResponse.data.success) {
                throw new Error('Failed to update meeting with whiteboard ID');
            }
            return updateResponse.data.data;
        }
        // Create a new whiteboard for the meeting
        const whiteboard = await createWhiteboard(meeting_id);
        // Update the meeting with the new whiteboard ID
        const updateResponse = await axiosInstance.put(`/meetings/${meeting_id}`, {
            active_whiteboard_id: whiteboard.id
        });
        if (!updateResponse.data.success) {
            throw new Error('Failed to update meeting with whiteboard ID');
        }
        return updateResponse.data.data;
    }
    catch (error) {
        return rejectWithValue(getUserFacingMessageFromAxiosError(error, { fallback: 'An error occurred while ensuring meeting has a whiteboard' }));
    }
});
// Initial state
const initialState = {
    current_meeting: null,
    all_meetings: [], // Initialize empty array
    status: 'idle',
    error: null
};
// Create the slice
const meeting_slice = createSlice({
    name: 'meeting',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(get_meetings.pending, (state) => {
            state.status = 'loading';
        })
            .addCase(get_meetings.fulfilled, (state, action) => {
            state.status = 'succeeded';
            state.all_meetings = action.payload;
            state.error = null;
        })
            .addCase(get_meetings.rejected, (state, action) => {
            state.status = 'failed';
            state.error = action.payload || 'Failed to fetch meetings';
        })
            .addCase(start_meeting.pending, (state) => {
            state.status = 'loading';
        })
            .addCase(start_meeting.fulfilled, (state, action) => {
            state.status = 'succeeded';
            state.current_meeting = action.payload;
            state.error = null;
        })
            .addCase(start_meeting.rejected, (state, action) => {
            state.status = 'failed';
            state.error = action.payload || 'An error occurred';
        })
            .addCase(join_meeting.pending, (state) => {
            state.status = 'loading';
        })
            .addCase(join_meeting.fulfilled, (state, action) => {
            state.status = 'succeeded';
            state.current_meeting = action.payload;
            state.error = null;
        })
            .addCase(join_meeting.rejected, (state, action) => {
            state.status = 'failed';
            state.error = action.payload || 'An error occurred';
        })
            .addCase(end_meeting.pending, (state) => {
            state.status = 'loading';
        })
            .addCase(end_meeting.fulfilled, (state) => {
            state.status = 'succeeded';
            state.current_meeting = null;
            state.error = null;
        })
            .addCase(end_meeting.rejected, (state, action) => {
            state.status = 'failed';
            state.error = action.payload || 'An error occurred';
        })
            .addCase(leave_meeting.pending, (state) => {
            state.status = 'loading';
        })
            .addCase(leave_meeting.fulfilled, (state) => {
            state.status = 'succeeded';
            state.current_meeting = null;
            state.error = null;
        })
            .addCase(leave_meeting.rejected, (state, action) => {
            state.status = 'failed';
            state.error = action.payload || 'An error occurred';
        })
            .addCase(delete_meeting.pending, (state) => {
            state.status = 'loading';
        })
            .addCase(delete_meeting.fulfilled, (state) => {
            state.status = 'succeeded';
            state.current_meeting = null;
            state.error = null;
        })
            .addCase(delete_meeting.rejected, (state, action) => {
            state.status = 'failed';
            state.error = action.payload || 'An error occurred';
        })
            .addCase(restore_meeting.pending, (state) => {
            state.status = 'loading';
        })
            .addCase(restore_meeting.fulfilled, (state, action) => {
            state.status = 'succeeded';
            state.current_meeting = action.payload;
            state.error = null;
        })
            .addCase(restore_meeting.rejected, (state, action) => {
            state.status = 'failed';
            state.error = action.payload || 'An error occurred';
        })
            .addCase(ensure_meeting_whiteboard.pending, (state) => {
            state.status = 'loading';
        })
            .addCase(ensure_meeting_whiteboard.fulfilled, (state, action) => {
            state.status = 'succeeded';
            state.current_meeting = action.payload;
            state.error = null;
        })
            .addCase(ensure_meeting_whiteboard.rejected, (state, action) => {
            state.status = 'failed';
            state.error = action.payload || 'An error occurred ensuring whiteboard';
        });
    }
});
export default meeting_slice.reducer;
